/**
 * @module  Experience/Softgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// Core Libraries
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Dependent Services
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Constants
import { SOFTGOODS_MODULE_COMPONENTS } from '@experience/shop/softgoods/constants/softgoods.constants';
import { PRODUCT_SELECTOR_CONSTANTS } from '@experience/shop/product-selector/constants/product-selector.constants';
import { LoggerService } from '@core/logger/services/logger.service';

// Interfaces
import {
  Cart,
  CartResponse,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';

// Models
import { ByodObject } from '@experience/shop/byod/interfaces/byod-landing.interface';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
/*tslint:disable:max-file-line-count */
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';

@Injectable({
  providedIn: 'root',
})
export class SoftgoodsService {
  public billingType: string;
  public cartData: Cart;
  public planUpdate$: Observable<boolean>;
  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public planUpdateSource: Subject<boolean>;

  constructor(
    private objectService: ObjectService,
    private productSelectorService: ProductSelectorService,
    private contentProviderService: ContentProviderService,
    private cartSupportService: CartSupportService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private sharedBrowseService: SharedBrowseService,
    private logger: LoggerService
  ) {
    this.planUpdateSource = new Subject();
    this.planUpdate$ = this.planUpdateSource.asObservable();
  }

  /**
   * Gets Billing Type for Plan Detail Page
   * @returns string
   */
  public getBillingType(): string {
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'entering getBillingType() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving getBillingType() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
    return this.sharedBrowseService.getBillingTypeAPIMapping(customerConfigData.billingType);
  }

  /**
   * Loads AEM content for browsePlans Landing page
   * @returns state and customer config
   */
  public loadSoftgoodsAEMContent(customerConfig: BrowseEntry): Observable<object> {
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'entering loadSoftgoodsAEMContent() in ' +
        SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE +
        ' customer Config: ' +
        JSON.stringify(customerConfig)
    );
    const prefix = this.sharedBrowseService.getBillingTypeAPIMapping(
      this.objectService.get(customerConfig, 'billingType', 0)
    );
    this.billingType = prefix.toLowerCase();
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving loadSoftgoodsAEMContent() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving loadSoftgoodsAEMContent() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
    return this.contentProviderService.getContentData(null, prefix.toLowerCase());
  }

  /**
   * Loads AEM content for plan-detail page
   * @returns state and customer config
   */
  public loadPlanDetailAEMContent(customerConfig: BrowseEntry): Observable<object> {
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'entering loadPlanDetailAEMContent() in ' +
        SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE +
        ' input params customerConfig: ' +
        JSON.stringify(customerConfig)
    );
    const prefix = this.sharedBrowseService.getBillingTypeAPIMapping(customerConfig.billingType);
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving loadPlanDetailAEMContent() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
    return this.contentProviderService.getContentData(
      CONTENT_ENDPOINT.PLAN_DETAILS,
      prefix.toLowerCase()
    );
  }

  /**
   * MEthod to check if device exists in cart
   * @returns boolean
   */
  public isDeviceExistInCart(): boolean {
    let cartLine = [];
    const currentCart = this.stateStoreUtilsService.getCart();
    if (currentCart && currentCart.lines) {
      cartLine = currentCart.lines.filter((line: CartLine) => {
        return line.id === this.stateStoreUtilsService.getLineIdForTileIdInFocus();
      });
    }
    return cartLine.length > 0 && cartLine[0].device && cartLine[0].device.id;
  }

  /**
   * check productselector is true or false and isSimNotNeeded is true or false
   * @param ByodObject
   * @returns boolean
   */
  public conditionCheck(
    isProductSelector: boolean,
    isAddLine: boolean,
    byodData: ByodObject
  ): boolean {
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      `entering conditionCheck() in ${SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE}`,
      `, input param isProductSelector: ${isProductSelector} || isAddLine ${isAddLine}`,
      `|| byodData ${JSON.stringify(byodData)}`
    );

    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving getAccessoriesByQuantity() in ' +
        SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE +
        ' returning boolean for : ' +
        JSON.stringify(
          isProductSelector &&
            !isAddLine &&
            !(byodData && byodData.simData && byodData.simData.isSimNotNeeded)
        )
    );
    return (
      isProductSelector &&
      !isAddLine &&
      !(byodData && byodData.simData && byodData.simData.isSimNotNeeded)
    );
  }

  /**
   * Method to access api
   *  @returns state and customer config
   */
  // @TODO remove following function
  public setBrowsePlanData(cartResponse: CartResponse): void {
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'entering setBrowsePlanData() in ' +
        SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE +
        ' input param cartResponse: ' +
        JSON.stringify(cartResponse)
    );
    this.cartData = cartResponse.cart;
    this.stateStoreUtilsService.setCart(this.cartData);
    this.stateStoreUtilsService.setEntryPoint(PRODUCT_SELECTOR_CONSTANTS.PLAN_FIRST);
    this.logger.debug(
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_MODULE_NAME,
      SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE,
      'leaving setBrowsePlanData() in ' + SOFTGOODS_MODULE_COMPONENTS.SOFTGOODS_SERVICE
    );
  }

  /**
   * Method to get rate plans
   *  @returns family plan
   */
  public updateTiles(selectedlineId: string, isProductSelector: boolean): void {
    const activeTiles = this.productSelectorService.getActiveTiles() || [];
    if (this.stateStoreUtilsService.isDefaultTileIdInFocus()) {
      const newTileId = this.cartSupportService.generateRandomTileID();
      activeTiles.splice(activeTiles.indexOf(SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED), 1);
      activeTiles.push(newTileId);
      // since tempTileId is being moved, cached vales should be moved next two lines does that
      const byodRestoreModel = this.stateStoreUtilsService.getByodRestoreModel(
        SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED
      );
      this.stateStoreUtilsService.setByodRestoreModel(byodRestoreModel, newTileId);
      if (!isProductSelector) {
        this.stateStoreUtilsService.setEntryPoint(PRODUCT_SELECTOR_CONSTANTS.BYOD_FIRST, newTileId);
      }
      this.productSelectorService.announceTileInFocusUpdate(newTileId);
    }
  }
}
